import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { AccessGroupProfile } from "../models/AccessGroupProfile";
import { genericError } from "../models/GenericError";
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";

const apiConnector = new APIConnector();

export const storeAccessGroupProfile = async (newGroupProfile: any, createAccessGroupProfile = true): Promise<boolean> => {
    try {
        const url = `accessGroupProfile`;
        let response: Response;

        await apiConnector.sendData(url, createAccessGroupProfile, newGroupProfile).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }

            const json = await response.json();

            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteAccessGroupProfile = async (profileId: string, accessGroupId: string): Promise<boolean> => {
    try {
        // First lookup the MasterGroup
        const profileGroups = await getAccessGroupProfiles(profileId, accessGroupId);

        // Found the MasterGroup, let's delete it then.
        if (profileGroups) {            
            const masterGroupId = profileGroups[0].id;
            const url = `accessGroupProfile/` + masterGroupId;
            let response: Response;

            await apiConnector.deleteData(url, buildToBeDeleteProfileGroups(profileGroups[0])).then(async (data) => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject({ status: response.status, message: genericError })
                })
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                const json = await response.json();

                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            });
        }
        // Unable to delete the MasterGroup. Couldn't find it.
        else {
            return new Promise<boolean>(async (resolve, reject) => {
                reject({ status: INTERNAL_SERVER_ERROR, message: genericError });
            });
        }
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessGroupProfiles = async (accessProfileId: string, accessGroupId: string): Promise<AccessGroupProfile[]> => {
    try {
        const url = `accessGroupProfile?filter=accessProfile.id==${accessProfileId};accessGroup.id==${accessGroupId}&include=accessGroup,accessProfile`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, apiAccessProfile: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    
                    const accessGroups: AccessGroupProfile[] = apiAccessProfile.map((d): AccessGroupProfile => {
                        return {
                            id: d['id'],
                            accessGroupId: d['access-group']['id'],
                            accessProfileId: d['access-profile']['id']
                        };
                    });

                    return resolve(accessGroups);
                },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    } 
}

export const getAccessGroupIdsForAccessProfile = async (accessProfileId: string): Promise<string[]> => {
    try {
        const url = 'accessGroupProfile?include=accessGroup&filter=accessProfile.id==' + accessProfileId;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, accessGroupProfiles: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const accessGroupIds: string[] = accessGroupProfiles.map((m) => {
                    return m['access-group']['id']
                });

                return resolve(accessGroupIds);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

//Gets Access Groups only
export const getAccessProfileAccessGroupsById = async (ids: string): Promise<string[]> => {
    try {
        const url = `accessGroupProfile?include=accessGroup,accessProfile&filter=accessProfile.id=in=(${ids})`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, apiAccessProfile: any[]) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }

                    const groups: string[] = apiAccessProfile.map((m) => {
                        return m['access-group']['id']
                    });

                    return resolve(groups);
                },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    } 
}

export const assignUnassignAccessProfileGroups = async (toBeAssigned: string[], toBeUnassigned: string[], accessProfileId: string) => {
    const newAccessGroupProfiles = buildToBeSavedProfileGroups(toBeAssigned, accessProfileId);
    let isAddSuccess: boolean = false
    
    // Create new accessGroupProfile
    try { 
        for (const newAccessGroupProfile of newAccessGroupProfiles) {
           const response = await storeAccessGroupProfile(newAccessGroupProfile, true);
           isAddSuccess = isAddSuccess && response;
        }

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }

    // Delete accessGroupProfile
    const removedAccessGroupProfiles = toBeUnassigned.map(async (accessGroupId) => {
        return await deleteAccessGroupProfile(accessProfileId, accessGroupId)
    })
   

    return Promise.all([isAddSuccess, ...removedAccessGroupProfiles]);
}

//Returns array of accessGroup Relationships to be saved
const buildToBeSavedProfileGroups = (toBeAssigned: string[], accessProfileId: string) => {
    return toBeAssigned.map((accessGroupId) => {
        return ({
            data: {
                id: 0,
                type: "accessGroupProfile",
                relationships: {
                    accessProfile: {
                        data: {
                            type: "accessProfile",
                            id: accessProfileId,
                        },
                    },
                    accessGroup: {
                        data: {
                            type: "accessGroup",
                            id: accessGroupId,
                        },
                    },
                },
            },
        })
    })
}

//Return array of accessGroup relationships to be deleted
const buildToBeDeleteProfileGroups = (accessGroupProfile: AccessGroupProfile) => {
    return {
        data: {
            id: accessGroupProfile.id,
            type: "accessGroupProfile",
            relationships: {
                accessProfile: {
                    data: {
                        type: "accessProfile",
                        id: accessGroupProfile.accessProfileId,
                    },
                },
                accessGroup: {
                    data: {
                        type: "accessGroup",
                        id: accessGroupProfile.accessGroupId,
                    },
                },
            },
        },
    }
}

export const getAccessProfileSites = async (accessProfileId: string): Promise<string> => {
    try {
        let url = `accessGroupProfile?include=accessGroup.site&filter=accessProfile.id==${accessProfileId}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }
            new Deserializer().deserialize(
                json,
                (err: any, result: any) => {
                    if (err) {
                        return reject({
                            status: response.status,
                            message: genericError,
                        });
                    }
                                                             
                    return resolve("");
                },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}